/**
 * hlf-milestone-upsell.js
 * Intercepts checkout clicks to show a milestone upsell bottom sheet
 * if the user hasn't reached the first milestone yet.
 */

(function() {
  const SELECTORS = {
    checkoutButtons: '.gokwik-checkout button, .cart__checkout-button',
    overlay: '#hlf-milestone-upsell',
    skipBtn: '#hlf-mu-skip-btn',
    carouselTrack: '#hlf-mu-carousel-track',
    gapAmount: '#hlf-mu-gap-amount',
    closeHandle: '.hlf-mu-drag-handle',
    productData: '#hlf-upsell-products-data'
  };

  let productsPool = [];
  let tiersData = [];
  let interceptedCheckoutBtn = null;
  let isInterceptActive = false;
  let isInitialized = false;

  // Initialize
  function init() {
    if (isInitialized) return;
    const dataEl = document.querySelector(SELECTORS.productData);
    if (!dataEl) return;

    try {
      productsPool = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Failed to parse upsell products:', e);
      return;
    }

    // Parse milestone tiers
    const tiersDataEl = document.querySelector('[data-tiers]');
    if (tiersDataEl) {
      try {
        const parsedTiers = JSON.parse(tiersDataEl.getAttribute('data-tiers'));
        if (parsedTiers && parsedTiers.length > 0) {
          tiersData = parsedTiers;
        }
      } catch (e) {
        console.error('Failed to parse milestone tiers:', e);
      }
    }

    // If milestone isn't found, we can't do much
    if (tiersData.length === 0) return;

    bindEvents();
    isInitialized = true;
  }

  function bindEvents() {
    // Intercept checkout clicks (event delegation on body to handle dynamic buttons)
    document.body.addEventListener('click', function(e) {
      // Find closest checkout button
      const btn = e.target.closest(SELECTORS.checkoutButtons);
      if (btn && !isInterceptActive && !btn.hasAttribute('data-upsell-bypassed')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        interceptCheckout(btn);
      }
    }, true);

    const overlay = document.querySelector(SELECTORS.overlay);
    if (overlay) {
      // Skip button continues to checkout
      const skipBtn = overlay.querySelector(SELECTORS.skipBtn);
      if (skipBtn) {
        skipBtn.addEventListener('click', function() {
          closeModal();
          proceedToCheckout();
        });
      }

      // Close handles just close modal
      const handle = overlay.querySelector(SELECTORS.closeHandle);
      const desktopClose = overlay.querySelector('.hlf-mu-close-btn');
      if (handle) {
        handle.addEventListener('click', closeModal);
      }
      if (desktopClose) {
        desktopClose.addEventListener('click', closeModal);
      }

      // Close on overlay background click
      overlay.addEventListener('click', function(e) {
        if (e.target === overlay) {
          closeModal();
        }
      });
    }
  }

  function interceptCheckout(btn) {
    interceptedCheckoutBtn = btn;
    
    // Fetch latest cart
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(res => res.json())
      .then(cart => {
        // Find the FIRST tier the user has NOT reached
        const nextTier = tiersData.find(tier => cart.total_price < tier.paise);

        if (nextTier) {
          const gap = nextTier.paise - cart.total_price;
          showUpsellModal(gap, cart, nextTier);
        } else {
          // All milestones reached, bypass
          proceedToCheckout();
        }
      })
      .catch(err => {
        console.error('Error fetching cart:', err);
        proceedToCheckout();
      });
  }

  function showUpsellModal(gap, cart, nextTier) {
    isInterceptActive = true;
    
    // Update gift info
    const giftNameEl = document.getElementById('hlf-mu-gift-name');
    const giftImgEl = document.getElementById('hlf-mu-gift-img');
    
    if (giftNameEl) {
      giftNameEl.textContent = nextTier.label || 'your free gift';
    }
    
    if (giftImgEl) {
      if (nextTier.image) {
        giftImgEl.src = nextTier.image;
        giftImgEl.style.display = 'block';
      } else {
        giftImgEl.style.display = 'none';
      }
    }

    // Format gap currency (assuming format is like ₹XXX)
    const gapStr = Shopify.formatMoney ? Shopify.formatMoney(gap, '₹{{amount}}').replace('.00', '') : '₹' + (gap / 100).toFixed(0);
    const gapEl = document.querySelector(SELECTORS.gapAmount);
    if (gapEl) gapEl.textContent = gapStr;

    // Filter and sort products
    // We want to prioritize products whose price >= gap, 
    // sorting them so the ones closest to the gap are first.
    // If not enough products >= gap, just fill with highest price products.
    let sortedProducts = [...productsPool];
    
    // Remove products already in cart
    const cartIds = cart.items.map(i => i.variant_id);
    sortedProducts = sortedProducts.filter(p => !cartIds.includes(p.id));

    sortedProducts.sort((a, b) => {
      const aSatisfies = a.price >= gap;
      const bSatisfies = b.price >= gap;

      if (aSatisfies && !bSatisfies) return -1;
      if (!aSatisfies && bSatisfies) return 1;
      
      // If both satisfy or neither satisfy, sort by closeness to gap (ascending)
      if (aSatisfies && bSatisfies) {
        return a.price - b.price; // Lowest price that satisfies gap
      } else {
        return b.price - a.price; // Highest price to get as close as possible
      }
    });

    // Render top 15 products
    renderCarousel(sortedProducts.slice(0, 15));

    // Show modal
    const overlay = document.querySelector(SELECTORS.overlay);
    if (overlay) {
      overlay.classList.add('hlf-mu-active');
    }
  }

  function renderCarousel(products) {
    const track = document.querySelector(SELECTORS.carouselTrack);
    if (!track) return;

    if (products.length === 0) {
      track.innerHTML = '<p style="padding:20px;text-align:center;width:100%;">No recommendations found.</p>';
      return;
    }

    const html = products.map(p => {
      const priceStr = Shopify.formatMoney ? Shopify.formatMoney(p.price, '₹{{amount}}').replace('.00', '') : '₹' + (p.price / 100).toFixed(0);
      const compareStr = p.compare_at_price > p.price 
        ? `<s style="font-size:10px;color:#94a3b8;margin-left:4px;">${Shopify.formatMoney ? Shopify.formatMoney(p.compare_at_price, '₹{{amount}}').replace('.00', '') : '₹' + (p.compare_at_price / 100).toFixed(0)}</s>` 
        : '';
        
      return `
        <div class="hlf-mu-product-card">
          <a href="${p.url}" class="hlf-mu-img-wrap" target="_blank" tabindex="-1">
            <img src="${p.image}" alt="${p.title}" loading="lazy" />
          </a>
          <div class="hlf-mu-prod-info">
            <a href="${p.url}" class="hlf-mu-prod-title" target="_blank">${p.title}</a>
            <div class="hlf-mu-prod-price">${priceStr}${compareStr}</div>
          </div>
          <button type="button" class="hlf-mu-add-btn js-mu-add-btn" data-id="${p.id}">Add</button>
        </div>
      `;
    }).join('');

    track.innerHTML = html;

    // Bind add buttons
    track.querySelectorAll('.js-mu-add-btn').forEach(btn => {
      btn.addEventListener('click', handleAddToCart);
    });
  }

  function handleAddToCart(e) {
    const btn = e.currentTarget;
    const id = btn.getAttribute('data-id');
    
    // Show loading state
    const originalText = btn.innerHTML;
    btn.innerHTML = '<span class="spinner" style="width:14px;height:14px;border:2px solid #fff;border-top-color:transparent;border-radius:50%;display:inline-block;animation:spin .6s linear infinite;"></span>';
    btn.disabled = true;

    // Find cart drawer to get sections to render
    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer && cartDrawer.getSectionsToRender 
      ? cartDrawer.getSectionsToRender().map(s => s.id) 
      : ['cart-drawer', 'cart-icon-bubble'];

    let formData = {
      'items': [{
        'id': id,
        'quantity': 1
      }],
      'sections': sections,
      'sections_url': window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      return response.json();
    })
    .then(parsedState => {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hlf-milestone-upsell', cartData: parsedState });
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState);
      } else {
        window.location.reload();
      }
      
      closeModal();
    })
    .catch((error) => {
      console.error('Error adding to cart:', error);
      btn.innerHTML = originalText;
      btn.disabled = false;
    });
  }

  function closeModal() {
    isInterceptActive = false;
    const overlay = document.querySelector(SELECTORS.overlay);
    if (overlay) {
      overlay.classList.remove('hlf-mu-active');
    }
  }

  function proceedToCheckout() {
    if (interceptedCheckoutBtn) {
      // Add bypass attribute to prevent re-interception
      interceptedCheckoutBtn.setAttribute('data-upsell-bypassed', 'true');
      interceptedCheckoutBtn.click();
      
      // Remove bypass attribute after a short delay in case checkout fails/cancels
      setTimeout(() => {
        interceptedCheckoutBtn.removeAttribute('data-upsell-bypassed');
      }, 1000);
    } else {
      window.location.href = '/checkout';
    }
  }

  // Add global styles for spinner if not present
  if (!document.getElementById('hlf-mu-spinner-styles')) {
    const style = document.createElement('style');
    style.id = 'hlf-mu-spinner-styles';
    style.innerHTML = '@keyframes spin { 100% { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
